import { Component } from '@verndale/core';
import { ReplaceHeading, ConditionalDisplay, ConvertUsStateAbbrAndName } from '../helpers/global';
import { splide } from '../helpers/splide';

type jobType = {
  estimatedStartDate: string;
  createdDate: string;
  profession: string;
  specialties: { specialtyName: string }[];
  city: string;
  worksiteState: string;
  assignmentLength: string;
  gross: number;
  localPay: { localPayWeeklyGross: number };
  id: string;
};

class AvailJob extends Component {
  setupDefaults() {
    this.dom = {
      heading: this.el.querySelector<HTMLElement>('.v-section-heading'),
      card: this.el.querySelector<HTMLElement>('.v-section-job__card'),
      list: this.el.querySelector<HTMLElement>('.v-section__job-list')
    };

    if (ConditionalDisplay(this.el.id)) {
      this.el.style.display = 'none';
    }
    if (this.dom.heading) {
      ReplaceHeading(this.dom.heading as HTMLElement);
    }

    this.getJobData();
  }

  getJobData() {
    const name = document.body.getAttribute('data-name') || '';
    const state = document.body.getAttribute('data-state') || '';
    const searchKey = '820ef5a2f48c47388a1b465fc8a51c8c';
    const searchURL = `https://hc-api-mgmt.azure-api.net/hcenterprisematching/api/search/search?subscription-key=${searchKey}`;
    if (state) return;

    const body = {
      searchParameters: {
        lineOfBusiness: 'HCTN',
        states: [ConvertUsStateAbbrAndName(name)]
      },
      limit: 20,
      offset: 0,
      orderby: 4
    };

    fetch(searchURL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    })
      .then(response => response.json())
      .then(data => {
        if (data.jobs.length > 0) {
          this.createCards(data.jobs);
          this.updateParentJob(data.jobs);
        }
      })
      .catch(error => {
        console.error('Error:', error);
      });
  }

  addCommas(number: number) {
    const parts = number.toFixed(2).toString().split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    if (parts[1] && Number(parts[1]) < 1) {
      parts.pop();
    }

    return parts.join('.');
  }

  createCards(jobs: jobType[]) {
    if (this.dom.list === null) return;
    (this.dom.list as HTMLElement).innerHTML = '';
    jobs.forEach((job: jobType) => {
      const newCard = (this.dom.card as HTMLElement).cloneNode(true) as HTMLElement;

      const rawStartDate = new Date(job.estimatedStartDate);
      const rawCreationDate = new Date(job.createdDate);
      const startDate = `${rawStartDate.getMonth() + 1}/${rawStartDate.getDate()}/${rawStartDate.getFullYear()}`;
      const postDate = `${rawCreationDate.getMonth() + 1}/${rawCreationDate.getDate()}/${rawCreationDate.getFullYear()}`;
      const jobDuration =
        +job.assignmentLength > 1
          ? `${job.assignmentLength} weeks`
          : `${job.assignmentLength} week`;
      const localJobs = false;
      const localOrTravel = localJobs ? 'Local' : 'Travel';
      const jobTitle =
        !job.specialties[0] || job.profession === job.specialties[0].specialtyName
          ? `Travel ${job.profession}`
          : `Travel ${job.profession} ${job.specialties[0].specialtyName}`;
      const payrate = localJobs
        ? job.localPay.localPayWeeklyGross
          ? `$${this.addCommas(job.localPay.localPayWeeklyGross)}`
          : `Unlisted`
        : job.gross
          ? `$${this.addCommas(job.gross)}`
          : `Unlisted`;

      const ondemandLink = `https://ondemand.hctravelnursing.com/jobs/${job.id}?interested=true`;

      newCard.querySelector('.v-section-job__favorite')?.setAttribute('href', ondemandLink);
      newCard.querySelector('.v-section-job__top-heading')!.textContent = jobTitle;
      newCard.querySelector('.v-section-job__flag')!.textContent = localOrTravel;
      newCard.querySelector('.v-section-job__location')!.textContent =
        `${job.city}, ${job.worksiteState}`;
      newCard.querySelector('.v-section__job-date')!.textContent = postDate;
      newCard.querySelector('.v-section-job__weekly')!.textContent = payrate;
      newCard.querySelector('.v-section-job__start')!.textContent = startDate;
      newCard.querySelector('.v-section-job__duration')!.textContent = jobDuration;
      const otherCard = document.createElement('div');

      newCard.style.position = 'absolute';
      newCard.style.insetBlockStart = '0';
      newCard.style.insetInlineStart = '0';
      newCard.style.insetBlockEnd = '0';
      newCard.style.insetInlineEnd = '0';
      otherCard.appendChild(newCard);
      (this.dom.list as HTMLElement).appendChild(otherCard);
      otherCard.style.position = 'relative';

      otherCard.classList.add('splide__slide');
    });
    if (jobs.length > 1) {
      this.createSplide();
    }
  }

  createSplide() {
    const splideOptions = {
      perPage: 2,
      padding: {
        left: '0',
        right: '30%'
      },

      breakpoints: {
        768: {
          perPage: 1,
          padding: {
            left: '0',
            right: '20%'
          }
        },
        1280: {
          perPage: 1,
          padding: {
            left: 0,
            right: '40%'
          }
        }
      }
    };

    const splideCreated = splide(this.el.querySelector('.splide') as HTMLElement, splideOptions);

    this.updateCardHeight();

    splideCreated.on('resized', () => {
      this.updateCardHeight();
    });
  }

  updateCardHeight() {
    let slideHeight = 0;
    const slides = this.el.querySelectorAll('.splide__slide');
    slides.forEach(slide => {
      const card = slide.querySelector('.v-section-job__card') as HTMLElement;
      slideHeight = Math.max(card.clientHeight, slideHeight);
    });

    slides.forEach(slide => {
      (slide as HTMLElement).style.height = `${slideHeight + 2}px`;
    });
  }

  updateParentJob(jobs: jobType[]) {
    if (jobs.length > 0) {
      const rawCreationDate = new Date(jobs[0].createdDate);
      const postDate = `${rawCreationDate.getMonth() + 1}/${rawCreationDate.getDate()}/${rawCreationDate.getFullYear()}`;
      const jobDuration =
        +jobs[0].assignmentLength > 1
          ? `${jobs[0].assignmentLength} weeks`
          : `${jobs[0].assignmentLength} week`;
      const localJobs = false;
      const jobTitle =
        !jobs[0].specialties[0] || jobs[0].profession === jobs[0].specialties[0].specialtyName
          ? `Travel ${jobs[0].profession}`
          : `Travel ${jobs[0].profession} ${jobs[0].specialties[0].specialtyName}`;
      const payrate = localJobs
        ? jobs[0].localPay.localPayWeeklyGross
          ? `$${this.addCommas(jobs[0].localPay.localPayWeeklyGross)}`
          : `Unlisted`
        : jobs[0].gross
          ? `$${this.addCommas(jobs[0].gross)}`
          : `Unlisted`;

      const jobData = {
        '@context': 'https://schema.org/',
        '@type': 'JobPosting',
        hiringOrganization: 'Please Contact for Facility Info',
        title: jobTitle,
        industry: 'Healthcare',
        employmentType: 'Contract',
        workHours: jobDuration,
        datePosted: postDate,
        jobLocation: {
          '@type': 'Place',
          address: {
            '@type': 'PostalAddress',
            addressLocality: jobs[0].city,
            addressRegion: jobs[0].worksiteState,
            addressCountry: 'USA'
          }
        },
        baseSalary: {
          '@type': 'MonetaryAmount',
          currency: 'USD',
          value: {
            '@type': 'QuantitativeValue',
            value: payrate,
            unitText: 'WEEK'
          }
        }
      };

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(jobData);
      document.head.appendChild(script);

      this.updateParentListing(jobs);
    }
  }

  updateParentListing(jobs: jobType[]) {
    const jobListing: {
      '@context': string;
      '@type': string;
      itemListElement: {
        '@type': string;
        position: number;
        name: string;
        url: string;
      }[];
    } = {
      '@context': 'https://schema.org/',
      '@type': 'ItemList',
      itemListElement: []
    };

    jobs.forEach((job, index) => {
      if (index === 0) return;

      const jobTitle =
        !job.specialties[0] || job.profession === job.specialties[0].specialtyName
          ? `Travel ${job.profession}`
          : `Travel ${job.profession} ${job.specialties[0].specialtyName}`;
      const ondemandLink = `https://ondemand.hctravelnursing.com/jobs/${job.id}?interested=true`;

      const jobObj = {
        '@type': 'ListItem',
        position: index,
        name: jobTitle,
        url: ondemandLink
      };

      jobListing.itemListElement.push(jobObj);
    });

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(jobListing);
    document.head.appendChild(script);
  }
}

export default AvailJob;
